import { useState, useEffect, useMemo } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Syringe, Shield, Droplet, Bug, ChevronDown, Plus,
  Trash2, CheckCircle, AlertTriangle, Clock, History,
  Edit, CheckSquare, X, Info, AlertCircle, Layers
} from "lucide-react";

// ============================================
// VACCINE KNOWLEDGE BASE - Combinati & Priorità
// ============================================

// Vaccini obbligatori per specie
const MANDATORY_VACCINES = {
  dog: ["rabies"],
  cat: ["rabies"],
  horse: ["rabies", "tetanus"],
  rabbit: ["myxomatosis", "rhd"]
};

// Mapping vaccini combinati -> componenti protetti
const COMBINED_VACCINES_MAP = {
  // EURICAN variants
  "eurican dhppi": ["distemper", "hepatitis", "parvovirus", "parainfluenza"],
  "eurican dappi": ["distemper", "adenovirus", "parvovirus", "parainfluenza"],
  "eurican lmulti": ["leptospirosis"],
  "eurican dhppi+l": ["distemper", "hepatitis", "parvovirus", "parainfluenza", "leptospirosis"],
  "eurican dappi+lmulti": ["distemper", "adenovirus", "parvovirus", "parainfluenza", "leptospirosis"],
  
  // NOBIVAC variants
  "nobivac dhppi": ["distemper", "hepatitis", "parvovirus", "parainfluenza"],
  "nobivac l4": ["leptospirosis"],
  "nobivac lepto": ["leptospirosis"],
  "nobivac kc": ["bordetella", "parainfluenza"],
  "nobivac tricat": ["panleukopenia", "calicivirus", "herpesvirus"],
  "nobivac rabies": ["rabies"],
  
  // VANGUARD
  "vanguard plus 5": ["distemper", "hepatitis", "parvovirus", "parainfluenza"],
  "vanguard 7": ["distemper", "hepatitis", "parvovirus", "parainfluenza", "leptospirosis"],
  
  // CANIGEN
  "canigen dhppi": ["distemper", "hepatitis", "parvovirus", "parainfluenza"],
  "canigen l": ["leptospirosis"],
  
  // PUREVAX (gatti)
  "purevax rcp": ["panleukopenia", "calicivirus", "herpesvirus"],
  "purevax rcpch": ["panleukopenia", "calicivirus", "herpesvirus", "chlamydia"],
  "purevax felv": ["feline_leukemia"],
  "purevax rabies": ["rabies"],
  
  // FELIGEN (gatti)
  "feligen crp": ["panleukopenia", "calicivirus", "herpesvirus"],
  
  // Generic patterns
  "dhpp": ["distemper", "hepatitis", "parvovirus", "parainfluenza"],
  "dhppi": ["distemper", "hepatitis", "parvovirus", "parainfluenza"],
  "dapp": ["distemper", "adenovirus", "parvovirus", "parainfluenza"],
  "dappi": ["distemper", "adenovirus", "parvovirus", "parainfluenza"],
  "da2pp": ["distemper", "adenovirus", "parvovirus", "parainfluenza"],
};

// Protection type display names (IT/EN)
const PROTECTION_LABELS = {
  distemper: "Cimurro",
  hepatitis: "Epatite",
  adenovirus: "Adenovirus (Epatite)",
  parvovirus: "Parvovirosi",
  parainfluenza: "Parainfluenza",
  leptospirosis: "Leptospirosi",
  rabies: "Rabbia",
  bordetella: "Bordetella (Tosse canili)",
  leishmaniasis: "Leishmaniosi",
  panleukopenia: "Panleucopenia",
  calicivirus: "Calicivirus",
  herpesvirus: "Herpesvirus (Rinotracheite)",
  chlamydia: "Chlamydia",
  feline_leukemia: "Leucemia Felina (FeLV)",
  myxomatosis: "Mixomatosi",
  rhd: "Malattia Emorragica (RHD)",
  tetanus: "Tetano"
};

// Category config with descriptions
const CATEGORY_CONFIG = {
  rabies: { 
    icon: Shield, 
    color: "#EF4444",
    description: "Protegge dalla rabbia, malattia mortale trasmissibile all'uomo",
    mandatory: true,
    priority: 1
  },
  dhpp: { 
    icon: Syringe, 
    color: "#3B82F6",
    description: "Protegge da Cimurro, Epatite, Parvovirosi e Parainfluenza",
    mandatory: false,
    priority: 2
  },
  ceppi_l4: { 
    icon: Droplet, 
    color: "#10B981",
    description: "Protegge dalla Leptospirosi (4 ceppi), infezione batterica",
    mandatory: false,
    priority: 3
  },
  lepto: { 
    icon: Droplet, 
    color: "#10B981",
    description: "Protegge dalla Leptospirosi, trasmessa da acqua contaminata",
    mandatory: false,
    priority: 3
  },
  parvo: { 
    icon: Bug, 
    color: "#8B5CF6",
    description: "Protegge dal Parvovirus, grave malattia gastrointestinale",
    mandatory: false,
    priority: 2
  },
  distemper: { 
    icon: Bug, 
    color: "#F59E0B",
    description: "Protegge dal Cimurro, malattia virale multisistemica",
    mandatory: false,
    priority: 2
  },
  leishmaniosi: { 
    icon: Shield, 
    color: "#EC4899",
    description: "Protegge dalla Leishmaniosi, trasmessa dai pappataci",
    mandatory: false,
    priority: 4
  },
  bordetella: { 
    icon: Syringe, 
    color: "#06B6D4",
    description: "Protegge dalla Tosse dei canili, infezione respiratoria",
    mandatory: false,
    priority: 5
  },
  felv: { 
    icon: Syringe, 
    color: "#F97316",
    description: "Protegge dalla Leucemia Felina (FeLV)",
    mandatory: false,
    priority: 3
  },
  trivalente_gatto: { 
    icon: Syringe, 
    color: "#14B8A6",
    description: "Protegge da Rinotracheite, Calicivirus e Panleucopenia",
    mandatory: false,
    priority: 2
  },
  combined: {
    icon: Layers,
    color: "#6366F1",
    description: "Vaccino combinato multi-protezione",
    mandatory: false,
    priority: 2
  },
  other: { 
    icon: Syringe, 
    color: "#6B7280",
    description: "Altri vaccini",
    mandatory: false,
    priority: 10
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return null;
  }
};

const formatDateInput = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.split('T')[0];
};

const isExpired = (dateStr) => {
  if (!dateStr) return false;
  try {
    return new Date(dateStr) < new Date();
  } catch {
    return false;
  }
};

const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  try {
    const expiry = new Date(dateStr);
    const now = new Date();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return expiry > now && (expiry - now) < thirtyDays;
  } catch {
    return false;
  }
};

// Detect if vaccine is combined and what it contains
const detectCombinedVaccine = (vaccineName) => {
  if (!vaccineName) return null;
  
  const nameLower = vaccineName.toLowerCase().trim();
  
  // Check exact matches first
  for (const [pattern, protections] of Object.entries(COMBINED_VACCINES_MAP)) {
    if (nameLower.includes(pattern)) {
      return {
        isCombined: protections.length > 1,
        protections,
        labels: protections.map(p => PROTECTION_LABELS[p] || p)
      };
    }
  }
  
  // Check for + sign (indicates combined)
  if (nameLower.includes('+') || nameLower.includes('multi') || nameLower.includes('penta') || nameLower.includes('hexa') || nameLower.includes('hepta')) {
    // Try to parse components
    const protections = [];
    if (nameLower.includes('lepto') || nameLower.includes('l4') || nameLower.includes('lmulti')) {
      protections.push('leptospirosis');
    }
    if (nameLower.includes('dhpp') || nameLower.includes('dapp') || nameLower.includes('da2pp')) {
      protections.push('distemper', 'hepatitis', 'parvovirus', 'parainfluenza');
    }
    if (nameLower.includes('rabbia') || nameLower.includes('rabies') || nameLower.includes('rabisin')) {
      protections.push('rabies');
    }
    
    if (protections.length > 1) {
      return {
        isCombined: true,
        protections,
        labels: protections.map(p => PROTECTION_LABELS[p] || p)
      };
    }
  }
  
  return null;
};

// Find overlapping protections between vaccines
const findOverlappingProtections = (allVaccines) => {
  const protectionMap = {}; // protection -> [vaccine names that provide it]
  
  allVaccines.forEach(vax => {
    const combined = detectCombinedVaccine(vax.name || vax.vaccine_name);
    if (combined) {
      combined.protections.forEach(prot => {
        if (!protectionMap[prot]) protectionMap[prot] = [];
        protectionMap[prot].push({
          name: vax.name || vax.vaccine_name,
          expiry: vax.expiry_date,
          isExpired: isExpired(vax.expiry_date)
        });
      });
    } else {
      // Single vaccine - try to detect protection from name/type
      const name = (vax.name || vax.vaccine_name || '').toLowerCase();
      const type = (vax.vaccine_type || '').toLowerCase();
      
      let protection = null;
      if (name.includes('lepto') || type === 'lepto') protection = 'leptospirosis';
      else if (name.includes('rabbia') || name.includes('rabies') || type === 'rabies') protection = 'rabies';
      else if (name.includes('parvo') || type === 'parvo') protection = 'parvovirus';
      else if (name.includes('cimurro') || name.includes('distemper') || type === 'distemper') protection = 'distemper';
      else if (name.includes('leishman') || type === 'leishmaniosi') protection = 'leishmaniasis';
      else if (name.includes('bordetella') || name.includes('tosse') || type === 'bordetella') protection = 'bordetella';
      
      if (protection) {
        if (!protectionMap[protection]) protectionMap[protection] = [];
        protectionMap[protection].push({
          name: vax.name || vax.vaccine_name,
          expiry: vax.expiry_date,
          isExpired: isExpired(vax.expiry_date)
        });
      }
    }
  });
  
  // Find overlaps (same protection from multiple vaccines)
  const overlaps = [];
  Object.entries(protectionMap).forEach(([protection, vaccines]) => {
    if (vaccines.length > 1) {
      overlaps.push({
        protection,
        label: PROTECTION_LABELS[protection] || protection,
        vaccines
      });
    }
  });
  
  return overlaps;
};

// ============================================
// MAIN COMPONENT
// ============================================

export default function VaccinesGrouped({ petId, petSpecies = 'dog', onAddVaccine }) {
  const [loading, setLoading] = useState(true);
  const [groupedData, setGroupedData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedVaccines, setSelectedVaccines] = useState(new Set());
  const [editingVaccine, setEditingVaccine] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [infoCategory, setInfoCategory] = useState(null);

  useEffect(() => {
    if (petId) fetchGroupedVaccines();
  }, [petId]);

  const fetchGroupedVaccines = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/vaccines/grouped/${petId}`);
      if (res.ok) {
        setGroupedData(await res.json());
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    } finally {
      setLoading(false);
    }
  };

  // Compute overlapping protections
  const overlappingProtections = useMemo(() => {
    if (!groupedData) return [];
    
    const allVaccines = [];
    Object.values(groupedData.categories || {}).forEach(cat => {
      (cat.history || []).forEach(v => allVaccines.push(v));
    });
    
    return findOverlappingProtections(allVaccines);
  }, [groupedData]);

  // Sort categories by priority (mandatory first)
  const sortedCategories = useMemo(() => {
    if (!groupedData?.categories) return [];
    
    const mandatoryForSpecies = MANDATORY_VACCINES[petSpecies] || MANDATORY_VACCINES.dog;
    
    return Object.entries(groupedData.categories).sort((a, b) => {
      const [catIdA] = a;
      const [catIdB] = b;
      
      const isMandatoryA = mandatoryForSpecies.includes(catIdA);
      const isMandatoryB = mandatoryForSpecies.includes(catIdB);
      
      // Mandatory vaccines first
      if (isMandatoryA && !isMandatoryB) return -1;
      if (!isMandatoryA && isMandatoryB) return 1;
      
      // Then by priority
      const priorityA = CATEGORY_CONFIG[catIdA]?.priority || 10;
      const priorityB = CATEGORY_CONFIG[catIdB]?.priority || 10;
      
      return priorityA - priorityB;
    });
  }, [groupedData, petSpecies]);

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const handleDelete = async (vaccineId) => {
    if (!confirm("Eliminare questo vaccino?")) return;
    try {
      const res = await authFetch(`${API}/vaccines/${vaccineId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success("Vaccino eliminato");
        fetchGroupedVaccines();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'eliminazione");
      }
    } catch {
      toast.error("Errore di connessione");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedVaccines.size === 0) return;
    if (!confirm(`Eliminare ${selectedVaccines.size} vaccini?`)) return;
    
    let deleted = 0;
    let errors = 0;
    for (const id of selectedVaccines) {
      try {
        const res = await authFetch(`${API}/vaccines/${id}`, { method: 'DELETE' });
        if (res.ok) deleted++;
        else errors++;
      } catch {
        errors++;
      }
    }
    
    if (deleted > 0) toast.success(`${deleted} vaccini eliminati`);
    if (errors > 0) toast.error(`${errors} vaccini non eliminati`);
    
    setSelectedVaccines(new Set());
    setSelectionMode(false);
    fetchGroupedVaccines();
  };

  const toggleSelect = (vaccineId) => {
    setSelectedVaccines(prev => {
      const newSet = new Set(prev);
      newSet.has(vaccineId) ? newSet.delete(vaccineId) : newSet.add(vaccineId);
      return newSet;
    });
  };

  const selectAllInCategory = (vaccines) => {
    setSelectedVaccines(prev => {
      const newSet = new Set(prev);
      vaccines.forEach(v => newSet.add(v.vaccine_id));
      return newSet;
    });
  };

  const handleEditVaccine = async (e) => {
    e.preventDefault();
    if (!editingVaccine) return;
    
    try {
      const res = await authFetch(`${API}/vaccines/${editingVaccine.vaccine_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pet_id: petId,
          name: editingVaccine.name,
          vaccine_type: editingVaccine.vaccine_type || 'altro',
          date_administered: editingVaccine.date_administered,
          expiry_date: editingVaccine.expiry_date || null,
          vet_name: editingVaccine.vet_name || null,
          batch_number: editingVaccine.batch_number || null
        })
      });
      
      if (res.ok) {
        toast.success("Vaccino aggiornato");
        setShowEditDialog(false);
        setEditingVaccine(null);
        fetchGroupedVaccines();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch {
      toast.error("Errore di connessione");
    }
  };

  const showCategoryInfo = (catId, catInfo) => {
    setInfoCategory({ id: catId, ...catInfo });
    setShowInfoDialog(true);
  };

  // Check if category is mandatory
  const isMandatory = (catId) => {
    const mandatoryForSpecies = MANDATORY_VACCINES[petSpecies] || MANDATORY_VACCINES.dog;
    return mandatoryForSpecies.includes(catId);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white dark:bg-[#2A2A2C] rounded-2xl p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!groupedData || groupedData.total_vaccines === 0) {
    return (
      <div className="bg-white dark:bg-[#2A2A2C] rounded-2xl p-8 text-center">
        <Syringe className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
        <p className="text-gray-500 dark:text-gray-400 mb-4">Nessun vaccino registrato</p>
        <Button onClick={onAddVaccine} className="bg-[#1D1D1F] dark:bg-white dark:text-black rounded-xl">
          <Plus size={16} className="mr-2" /> Aggiungi vaccino
        </Button>
      </div>
    );
  }

  const categories = groupedData.categories || {};

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">
            Vaccini ({groupedData.total_vaccines})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {groupedData.total_vaccines > 0 && (
            <Button
              size="sm"
              variant={selectionMode ? "default" : "outline"}
              onClick={() => {
                setSelectionMode(!selectionMode);
                if (selectionMode) setSelectedVaccines(new Set());
              }}
              className="rounded-xl text-xs h-8"
            >
              {selectionMode ? <X size={14} className="mr-1" /> : <CheckSquare size={14} className="mr-1" />}
              {selectionMode ? "Annulla" : "Seleziona"}
            </Button>
          )}
          
          {selectionMode && selectedVaccines.size > 0 && (
            <Button size="sm" variant="destructive" onClick={handleDeleteSelected} className="rounded-xl text-xs h-8">
              <Trash2 size={14} className="mr-1" />
              Elimina ({selectedVaccines.size})
            </Button>
          )}
          
          {!selectionMode && (
            <Button size="sm" onClick={onAddVaccine} className="bg-[#1D1D1F] dark:bg-white dark:text-black rounded-xl text-xs h-8">
              <Plus size={14} className="mr-1" /> Aggiungi
            </Button>
          )}
        </div>
      </div>

      {/* Overlapping Protections Warning */}
      {overlappingProtections.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={16} />
            <div className="text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                Protezioni sovrapposte rilevate
              </p>
              <ul className="text-amber-700 dark:text-amber-300 text-xs space-y-0.5">
                {overlappingProtections.slice(0, 3).map((overlap, i) => (
                  <li key={i}>
                    <strong>{overlap.label}</strong>: coperta da {overlap.vaccines.map(v => v.name).join(' e ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Categories - Sorted by priority */}
      {sortedCategories.map(([catId, category]) => {
        const catConfig = CATEGORY_CONFIG[catId] || CATEGORY_CONFIG.other;
        const Icon = catConfig.icon;
        const history = category.history || [];
        const latestVaccine = category.latest_vaccine;
        const isExpanded = expandedCategories[catId];
        const mandatory = isMandatory(catId);
        
        // Check if latest vaccine is combined
        const combinedInfo = detectCombinedVaccine(latestVaccine?.name || latestVaccine?.vaccine_name);
        
        // STATUS: Based ONLY on the LATEST vaccine's expiry
        const latestExpiry = latestVaccine?.expiry_date;
        const expired = latestExpiry && isExpired(latestExpiry);
        const expiringSoon = latestExpiry && isExpiringSoon(latestExpiry);
        
        // Status styling
        let statusBg, statusIcon, statusText;
        if (!latestExpiry) {
          statusBg = "border-gray-200 dark:border-gray-700";
          statusIcon = null;
          statusText = null;
        } else if (expired) {
          statusBg = "border-red-300 dark:border-red-500/50 bg-red-50/50 dark:bg-red-500/5";
          statusIcon = <AlertTriangle size={14} className="text-red-500" />;
          statusText = "Scaduto";
        } else if (expiringSoon) {
          statusBg = "border-amber-300 dark:border-amber-500/50 bg-amber-50/50 dark:bg-amber-500/5";
          statusIcon = <Clock size={14} className="text-amber-500" />;
          statusText = "In scadenza";
        } else {
          statusBg = "border-emerald-300 dark:border-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-500/5";
          statusIcon = <CheckCircle size={14} className="text-emerald-500" />;
          statusText = "Valido";
        }

        return (
          <div key={catId} className={`bg-white dark:bg-[#2A2A2C] rounded-2xl border-2 ${statusBg} overflow-hidden`}>
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(catId)}
              className="w-full p-3 flex items-center gap-3 text-left"
            >
              {selectionMode && (
                <Checkbox 
                  checked={history.every(v => selectedVaccines.has(v.vaccine_id))}
                  onCheckedChange={() => selectAllInCategory(history)}
                  onClick={(e) => e.stopPropagation()}
                  className="border-gray-400"
                />
              )}
              
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${catConfig.color}15` }}
              >
                <Icon size={20} style={{ color: catConfig.color }} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 text-sm">
                    {category.category_info?.display_name || catId}
                  </h4>
                  
                  {/* Mandatory badge */}
                  {mandatory && (
                    <span className="text-[10px] bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded font-medium">
                      OBBLIGATORIO
                    </span>
                  )}
                  
                  {/* Combined badge */}
                  {combinedInfo?.isCombined && (
                    <span className="text-[10px] bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                      <Layers size={10} /> COMBINATO
                    </span>
                  )}
                  
                  {statusIcon && (
                    <span className="flex items-center gap-1 text-xs">
                      {statusIcon}
                      <span className={
                        expired ? 'text-red-600 dark:text-red-400' : 
                        expiringSoon ? 'text-amber-600 dark:text-amber-400' : 
                        'text-emerald-600 dark:text-emerald-400'
                      }>
                        {statusText}
                      </span>
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); showCategoryInfo(catId, catConfig); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); showCategoryInfo(catId, catConfig); } }}
                    className="p-1 text-gray-400 hover:text-blue-500 cursor-pointer"
                  >
                    <Info size={14} />
                  </span>
                </div>
                
                {/* Show latest vaccine date range */}
                {latestVaccine && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatDate(latestVaccine.date_administered) || "—"}
                    {latestExpiry && ` → ${formatDate(latestExpiry)}`}
                  </p>
                )}
                
                {history.length > 1 && (
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                    <History size={10} />
                    {history.length} dosi
                  </p>
                )}
              </div>
              
              <ChevronDown size={18} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Expanded View */}
            {isExpanded && (
              <div className="border-t border-gray-100 dark:border-gray-700 p-2 bg-gray-50/50 dark:bg-black/20">
                {/* Combined vaccine contents */}
                {combinedInfo?.isCombined && (
                  <div className="mb-2 p-2.5 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl">
                    <p className="text-xs font-medium text-indigo-800 dark:text-indigo-200 mb-1.5 flex items-center gap-1">
                      <Layers size={12} /> Questo vaccino protegge da:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {combinedInfo.labels.map((label, i) => (
                        <span key={i} className="text-[10px] bg-white dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-200 px-2 py-0.5 rounded-full">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-1.5">
                  {history.map((vax, idx) => {
                    const isLatest = idx === 0;
                    const vaxExpiry = vax.expiry_date;
                    const vaxExpired = vaxExpiry && isExpired(vaxExpiry);
                    const isSelected = selectedVaccines.has(vax.vaccine_id);
                    const adminDate = formatDate(vax.date_administered);
                    const expiryDate = formatDate(vax.expiry_date);
                    const vaxCombined = detectCombinedVaccine(vax.name || vax.vaccine_name);
                    
                    return (
                      <div 
                        key={vax.vaccine_id || idx}
                        className={`p-2.5 rounded-xl transition-colors ${
                          isSelected 
                            ? 'bg-blue-100 dark:bg-blue-500/20 ring-2 ring-blue-500' 
                            : isLatest 
                              ? 'bg-white dark:bg-[#3A3A3C]' 
                              : 'bg-white/70 dark:bg-[#333335]'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {selectionMode && (
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleSelect(vax.vaccine_id)}
                              className="mt-0.5 border-gray-400"
                            />
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                {vax.name || vax.vaccine_name || "Vaccino"}
                              </span>
                              {isLatest && (
                                <span className="text-xs bg-blue-100 dark:bg-blue-500/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                  Ultimo
                                </span>
                              )}
                              {vax.is_booster && (
                                <span className="text-xs bg-purple-100 dark:bg-purple-500/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                                  Richiamo
                                </span>
                              )}
                            </div>
                            
                            {/* Dates */}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {adminDate || "Data non disponibile"}
                              {expiryDate && (
                                <>
                                  <span className="mx-1">→</span>
                                  <span className={isLatest && vaxExpired ? 'text-red-500 font-medium' : ''}>
                                    {expiryDate}
                                  </span>
                                  {isLatest && vaxExpired && (
                                    <span className="text-red-500 font-medium"> (scaduto)</span>
                                  )}
                                </>
                              )}
                            </p>
                            
                            {/* Show combined contents for non-latest */}
                            {!isLatest && vaxCombined?.isCombined && (
                              <p className="text-[10px] text-indigo-600 dark:text-indigo-400 mt-1">
                                Contiene: {vaxCombined.labels.join(', ')}
                              </p>
                            )}
                            
                            {vax.vet_name && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {vax.vet_name}
                              </p>
                            )}
                          </div>
                          
                          {!selectionMode && (
                            <div className="flex items-center gap-0.5">
                              <button 
                                onClick={() => { setEditingVaccine({...vax}); setShowEditDialog(true); }}
                                className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors"
                              >
                                <Edit size={14} />
                              </button>
                              <button 
                                onClick={() => handleDelete(vax.vaccine_id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 pt-1 flex-wrap">
        <span className="flex items-center gap-1">
          <CheckCircle size={12} className="text-emerald-500" /> Valido
        </span>
        <span className="flex items-center gap-1">
          <Clock size={12} className="text-amber-500" /> In scadenza
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle size={12} className="text-red-500" /> Scaduto
        </span>
        <span className="flex items-center gap-1">
          <Layers size={12} className="text-indigo-500" /> Combinato
        </span>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} /> Modifica Vaccino
            </DialogTitle>
          </DialogHeader>
          
          {editingVaccine && (
            <form onSubmit={handleEditVaccine} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Nome vaccino *</Label>
                <Input
                  value={editingVaccine.name || editingVaccine.vaccine_name || ""}
                  onChange={(e) => setEditingVaccine({...editingVaccine, name: e.target.value})}
                  className="mt-1 rounded-xl"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Data somministrazione *</Label>
                  <Input
                    type="date"
                    value={formatDateInput(editingVaccine.date_administered)}
                    onChange={(e) => setEditingVaccine({...editingVaccine, date_administered: e.target.value})}
                    className="mt-1 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Scadenza</Label>
                  <Input
                    type="date"
                    value={formatDateInput(editingVaccine.expiry_date)}
                    onChange={(e) => setEditingVaccine({...editingVaccine, expiry_date: e.target.value})}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Veterinario</Label>
                <Input
                  value={editingVaccine.vet_name || ""}
                  onChange={(e) => setEditingVaccine({...editingVaccine, vet_name: e.target.value})}
                  className="mt-1 rounded-xl"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Numero lotto</Label>
                <Input
                  value={editingVaccine.batch_number || ""}
                  onChange={(e) => setEditingVaccine({...editingVaccine, batch_number: e.target.value})}
                  className="mt-1 rounded-xl"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1 rounded-xl">
                  Annulla
                </Button>
                <Button type="submit" className="flex-1 bg-[#1D1D1F] dark:bg-white dark:text-black rounded-xl">
                  Salva
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent className="max-w-sm rounded-2xl">
          {infoCategory && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Info size={20} className="text-blue-500" />
                  {categories[infoCategory.id]?.category_info?.display_name || infoCategory.id}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {infoCategory.description}
                </p>
                {infoCategory.mandatory && (
                  <div className="bg-red-50 dark:bg-red-500/10 rounded-lg p-2 text-xs text-red-700 dark:text-red-300">
                    ⚠️ Questo vaccino è OBBLIGATORIO per legge
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
